interface BuildOnboardingIntentRequestInput {
    projectId: string;
    clientRequestId: string;
    journeySource: 'chat' | 'forward' | 'direct';
    chatSessionId: string | null;
    generatedProjectName: string;
    seedPaperId?: string | null;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function normalizeOnboardingSeedPaperId(
    value: string | string[] | null | undefined
): string | null {
    const candidate = Array.isArray(value) ? value[0] : value;
    return candidate && UUID_PATTERN.test(candidate) ? candidate : null;
}

export function withOnboardingSeedPaperId(
    destination: string,
    seedPaperId: string | null | undefined
): string {
    const normalizedSeedPaperId = normalizeOnboardingSeedPaperId(seedPaperId);
    if (!normalizedSeedPaperId) {
        return destination;
    }

    const [path, query = ''] = destination.split('?');
    const searchParams = new URLSearchParams(query);
    searchParams.set('seedPaperId', normalizedSeedPaperId);
    return `${path}?${searchParams.toString()}`;
}

export type StoredOnboardingIntentSeedDecision =
    | { kind: 'reuse' }
    | {
        kind: 'rebind';
        clientRequestId: string;
        seedPaperId: string;
    };

export function resolveStoredOnboardingIntentSeed({
    storedCreateClientRequestId,
    storedSeedPaperId,
    requestedSeedPaperId,
}: {
    storedCreateClientRequestId: string;
    storedSeedPaperId: string | null;
    requestedSeedPaperId: string | null;
}): StoredOnboardingIntentSeedDecision {
    if (!requestedSeedPaperId || storedSeedPaperId === requestedSeedPaperId) {
        return { kind: 'reuse' };
    }

    return {
        kind: 'rebind',
        clientRequestId: storedCreateClientRequestId,
        seedPaperId: requestedSeedPaperId,
    };
}

export interface OnboardingIntentRpcRequest {
    functionName:
        | 'create_or_resume_onboarding_project_intent'
        | 'create_or_resume_seeded_onboarding_project_intent';
    args: Record<string, string | null>;
}

export function hasExpectedOnboardingSeedBinding(
    value: unknown,
    expectedSeedPaperId: string | null | undefined
): boolean {
    if (!expectedSeedPaperId) {
        return true;
    }

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const response = value as Record<string, unknown>;
    return response.seed_paper_id === expectedSeedPaperId
        && response.seed_status === 'bound';
}

export function buildOnboardingIntentRpcRequest({
    projectId,
    clientRequestId,
    journeySource,
    chatSessionId,
    generatedProjectName,
    seedPaperId = null,
}: BuildOnboardingIntentRequestInput): OnboardingIntentRpcRequest {
    const args: Record<string, string | null> = {
        p_project_id: projectId,
        p_client_request_id: clientRequestId,
        p_journey_source: journeySource,
        p_chat_session_id: chatSessionId,
        p_generated_project_name: generatedProjectName,
    };

    if (seedPaperId) {
        args.p_seed_paper_id = seedPaperId;
    }

    return {
        functionName: seedPaperId
            ? 'create_or_resume_seeded_onboarding_project_intent'
            : 'create_or_resume_onboarding_project_intent',
        args,
    };
}
